/**
 * iter210 — Discrepancy Case Kanban board
 *
 * Columns: new → investigating → root_cause_assigned → corrected → approved → closed.
 * Each card shows SKU/location/variance + SLA badge. Manager can advance a
 * case from the card via the transition dialog. Admin can also edit the
 * SLA-per-state config from the header, and manually re-run the SLA watcher.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { ArrowRight, ClockCountdown, Gear, ArrowClockwise, Warning, Lightning, Trash, Plus } from '@phosphor-icons/react';
import {
  getDiscrepancyBoard, transitionCase, getDiscrepancySla, updateDiscrepancySla,
  runDiscrepancySlaWatcher, listCaseTemplates, createCaseTemplate,
  deleteCaseTemplate, applyCaseTemplate,
} from '../lib/api';

const STATE_LABELS = {
  new: 'New',
  investigating: 'Investigating',
  root_cause_assigned: 'Root cause',
  corrected: 'Corrected',
  approved: 'Approved',
  closed: 'Closed (14d)',
};

const STATE_HINTS = {
  new: 'Just opened · triage',
  investigating: 'Timeline being reviewed',
  root_cause_assigned: 'Cause identified',
  corrected: 'Stock fixed · needs sign-off',
  approved: 'Supervisor cleared',
  closed: 'Resolved · read-only',
};

const bandStyles = {
  breach: 'bg-red-50 border-red-300',
  warning: 'bg-amber-50 border-amber-300',
  ok: 'bg-white border-border',
};

function SlaBadge({ sla }) {
  if (!sla) return null;
  if (!sla.sla_hours) return null;
  const h = sla.hours_in_state ?? 0;
  const cls = sla.breach
    ? 'bg-red-100 text-red-800 border-red-300'
    : sla.band === 'warning'
      ? 'bg-amber-100 text-amber-800 border-amber-300'
      : 'bg-emerald-100 text-emerald-800 border-emerald-300';
  return (
    <Badge className={`text-[10px] ${cls}`} data-testid="case-sla-badge">
      <ClockCountdown size={10} className="mr-1" />
      {sla.breach ? 'SLA breach · ' : ''}{h}h / {sla.sla_hours}h
    </Badge>
  );
}

function CaseCard({ case_, allowedTransitions, reasonCodes, templates, onTransitioned, onDragStart, onDragEnd, isDragging }) {
  const [transDialog, setTransDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const nextStates = allowedTransitions[case_.state] || [];
  const draggable = case_.state !== 'closed' && (nextStates.length > 0);

  const openTransition = (target) => {
    setTransDialog({ target });
    setReason((reasonCodes[target] || [''])[0] || '');
    setNote('');
  };

  const submit = async () => {
    if (!reason) return toast.error('Pick a reason code');
    setBusy(true);
    try {
      await transitionCase(case_.id, { target_state: transDialog.target, reason_code: reason, note: note || null });
      toast.success(`Advanced → ${transDialog.target}`);
      setTransDialog(null);
      onTransitioned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = async (tpl) => {
    setBusy(true);
    setTemplatesOpen(false);
    try {
      await applyCaseTemplate(case_.id, { template_id: tpl.id });
      toast.success(`Applied "${tpl.name}" → ${tpl.target_state}`);
      onTransitioned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template apply failed');
    } finally {
      setBusy(false);
    }
  };

  const variance = case_.variance;
  const varianceCls = variance < 0 ? 'text-red-700' : variance > 0 ? 'text-emerald-700' : '';
  // Only surface templates whose target is reachable from the current state
  // AND not the current state itself. The backend will still validate.
  const applicableTemplates = (templates || []).filter((t) =>
    t.target_state !== case_.state && case_.state !== 'closed'
  ).slice(0, 5);

  return (
    <Card
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart?.(e, case_) : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      className={`p-3 border ${bandStyles[case_.sla?.band] || 'border-border'} space-y-1.5 ${
        draggable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${isDragging ? 'opacity-40 ring-2 ring-brand' : ''}`}
      data-testid={`board-card-${case_.id}`}
      data-draggable={draggable ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 justify-between">
        <div className="text-[10px] font-mono text-muted-foreground">{case_.case_number}</div>
        <SlaBadge sla={case_.sla} />
      </div>
      <div className="text-sm font-semibold" data-testid={`board-card-sku-${case_.id}`}>
        {case_.sku_code}
      </div>
      <div className="text-[11px] text-muted-foreground truncate" title={case_.sku_name}>
        {case_.sku_name}
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <Badge variant="outline" className="text-[9px]">{case_.location_code}</Badge>
        <span className={`font-mono ${varianceCls}`}>
          {variance > 0 ? '+' : ''}{variance} u
        </span>
        {typeof case_.variance_value === 'number' && case_.variance_value !== 0 && (
          <span className="text-muted-foreground">
            ${Math.abs(case_.variance_value).toLocaleString('en-AU', { maximumFractionDigits: 0 })}
          </span>
        )}
      </div>
      {case_.root_cause && (
        <div className="text-[10px]">
          <Badge className="text-[9px] bg-slate-100 text-slate-700 border-slate-300">
            {case_.root_cause.replace(/_/g, ' ')}
          </Badge>
        </div>
      )}

      {applicableTemplates.length > 0 && (
        <div className="relative">
          <Button
            size="xs"
            variant="outline"
            className="text-[10px] h-6 px-2 w-full"
            onClick={() => setTemplatesOpen((o) => !o)}
            disabled={busy}
            data-testid={`board-quick-advance-${case_.id}`}
          >
            <Lightning size={10} className="mr-1" weight="fill" />
            Quick advance ({applicableTemplates.length})
          </Button>
          {templatesOpen && (
            <div
              className="absolute z-10 mt-1 w-full bg-white border border-border rounded shadow-lg p-1 space-y-0.5"
              data-testid={`board-templates-menu-${case_.id}`}
            >
              {applicableTemplates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  className="w-full text-left px-2 py-1 text-[10px] hover:bg-slate-100 rounded"
                  data-testid={`board-template-apply-${case_.id}-${t.id}`}
                >
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-[9px] text-muted-foreground">
                    → {t.target_state.replace(/_/g, ' ')} · {t.reason_code.replace(/_/g, ' ')}
                    {t.use_count > 0 && ` · used ${t.use_count}×`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {nextStates.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {nextStates.map((s) => (
            <Button
              key={s}
              size="xs"
              variant="outline"
              className="text-[10px] h-6 px-2"
              onClick={() => openTransition(s)}
              data-testid={`board-transition-${case_.id}-${s}`}
            >
              <ArrowRight size={10} className="mr-1" />
              {s.replace(/_/g, ' ')}
            </Button>
          ))}
        </div>
      )}

      <Dialog open={!!transDialog} onOpenChange={(o) => !o && setTransDialog(null)}>
        <DialogContent data-testid="board-transition-dialog">
          <DialogHeader>
            <DialogTitle>
              Advance case → <span className="uppercase font-mono text-brand">
                {transDialog?.target?.replace(/_/g, ' ')}
              </span>
            </DialogTitle>
            <DialogDescription>
              Case {case_.case_number} · {case_.sku_code} @ {case_.location_code} · variance {variance}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason code *</Label>
              <select
                className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="board-transition-reason"
              >
                {(reasonCodes[transDialog?.target] || ['unspecified']).map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context…" data-testid="board-transition-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransDialog(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="board-transition-submit">
              {busy ? 'Advancing…' : 'Advance case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SlaEditor({ open, onClose, onSaved }) {
  const [hours, setHours] = useState(null);
  const [recipients, setRecipients] = useState({});
  const [defaults, setDefaults] = useState({});
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    getDiscrepancySla()
      .then((r) => {
        setHours(r.data.hours || {});
        setDefaults(r.data.defaults || {});
        setRecipients(r.data.recipients || {});
        setAvailable(r.data.available_recipients || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load SLA'));
  }, [open]);

  if (!open) return null;

  const toggleRecipient = (state, email) => {
    setRecipients((prev) => {
      const cur = prev[state] || [];
      const next = cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email];
      return { ...prev, [state]: next };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const cleanHours = {};
      Object.keys(hours || {}).forEach((k) => {
        const v = parseInt(hours[k], 10);
        if (v > 0) cleanHours[k] = v;
      });
      await updateDiscrepancySla({ hours: cleanHours, recipients });
      toast.success('SLA config saved');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="sla-editor-dialog">
        <DialogHeader>
          <DialogTitle>SLA per case state</DialogTitle>
          <DialogDescription>
            Time cases can sit in each state before Stockaris raises an escalation alert.
            Assign specific admins/managers per state so the right owner gets paged.
            A case that breaches nags at most once every 24h.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {hours && Object.keys(hours).map((state) => (
            <div key={state} className="border border-border rounded p-3 space-y-2" data-testid={`sla-row-${state}`}>
              <div className="flex items-center gap-3">
                <Label className="flex-1 text-sm capitalize font-semibold">
                  {state.replace(/_/g, ' ')}
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="720"
                  className="w-24"
                  value={hours[state]}
                  onChange={(e) => setHours({ ...hours, [state]: e.target.value })}
                  data-testid={`sla-input-${state}`}
                />
                <span className="text-xs text-muted-foreground w-16">
                  hrs (def {defaults[state]})
                </span>
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">
                  Escalate to
                  {(recipients[state] || []).length === 0
                    ? ' (using tenant default broadcast list)'
                    : ` · ${(recipients[state] || []).length} selected`}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {available.map((u) => {
                    const active = (recipients[state] || []).includes(u.email);
                    return (
                      <button
                        key={u.email}
                        type="button"
                        onClick={() => toggleRecipient(state, u.email)}
                        className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                          active
                            ? 'bg-brand text-white border-brand'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                        data-testid={`sla-recipient-${state}-${u.email}`}
                      >
                        {u.name} · {u.role}
                      </button>
                    );
                  })}
                  {available.length === 0 && (
                    <div className="text-[10px] text-muted-foreground italic">
                      No admins/managers found in this tenant.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="sla-editor-save">
            {busy ? 'Saving…' : 'Save SLA config'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateManager({ open, onClose, states, reasonCodes, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', target_state: 'root_cause_assigned', reason_code: '', note_template: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listCaseTemplates();
      setTemplates(r.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    // Default reason_code to the first canonical code when target_state changes
    const codes = reasonCodes?.[form.target_state] || [];
    if (codes.length > 0 && !codes.includes(form.reason_code)) {
      setForm((f) => ({ ...f, reason_code: codes[0] }));
    }
  }, [form.target_state, reasonCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const targetStates = (states || []).filter((s) => s !== 'new');
  const codes = reasonCodes?.[form.target_state] || [];

  const create = async () => {
    if (!form.name.trim() || !form.reason_code) return toast.error('Name + reason required');
    setBusy(true);
    try {
      await createCaseTemplate({
        name: form.name.trim(),
        target_state: form.target_state,
        reason_code: form.reason_code,
        note_template: form.note_template || null,
      });
      toast.success('Template created');
      setForm({ name: '', target_state: 'root_cause_assigned', reason_code: '', note_template: '' });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteCaseTemplate(id);
      toast.success('Template deleted');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle>Case templates</DialogTitle>
          <DialogDescription>
            One-tap presets for the transitions your team fires over and over. Applying
            a template fast-forwards a case through required states in a single click.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-border rounded p-3 space-y-2 bg-slate-50">
            <div className="text-xs font-semibold uppercase text-muted-foreground">
              New template
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Pick-scan bypass fix"
                  data-testid="template-form-name"
                />
              </div>
              <div>
                <Label className="text-xs">Target state</Label>
                <select
                  className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
                  value={form.target_state}
                  onChange={(e) => setForm({ ...form, target_state: e.target.value })}
                  data-testid="template-form-target"
                >
                  {targetStates.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Reason code</Label>
                <select
                  className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
                  value={form.reason_code}
                  onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                  data-testid="template-form-reason"
                >
                  {codes.map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Note template (optional)</Label>
                <Input
                  value={form.note_template}
                  onChange={(e) => setForm({ ...form, note_template: e.target.value })}
                  placeholder="Auto-note applied on triage…"
                  data-testid="template-form-note"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={create} disabled={busy} data-testid="template-create-submit">
                <Plus size={12} className="mr-1" /> {busy ? 'Creating…' : 'Create template'}
              </Button>
            </div>
          </div>

          <div className="text-xs font-semibold uppercase text-muted-foreground pt-2">
            Existing templates ({templates.length})
          </div>
          <div className="space-y-1.5 max-h-[35vh] overflow-y-auto pr-1">
            {loading && <div className="text-xs text-muted-foreground p-2">Loading…</div>}
            {!loading && templates.length === 0 && (
              <div className="text-xs text-muted-foreground italic p-2">
                No templates yet. Create one above.
              </div>
            )}
            {templates.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-2 p-2 border border-border rounded text-xs"
                data-testid={`template-row-${t.id}`}
              >
                <div className="flex-1">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    → <Badge variant="outline" className="text-[9px]">{t.target_state.replace(/_/g, ' ')}</Badge>{' '}
                    · reason <span className="font-mono">{t.reason_code}</span>
                    {t.use_count > 0 && ` · used ${t.use_count}×`}
                  </div>
                </div>
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-red-700 h-7"
                  onClick={() => remove(t.id)}
                  data-testid={`template-delete-${t.id}`}
                >
                  <Trash size={12} />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropTransitionDialog({ pending, reasonCodes, onClose, onCompleted }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pending) {
      setReason((reasonCodes?.[pending.target_state] || [''])[0] || '');
      setNote('');
    }
  }, [pending, reasonCodes]);

  if (!pending) return null;

  const submit = async () => {
    if (!reason) return toast.error('Pick a reason code');
    setBusy(true);
    try {
      await transitionCase(pending.case_id, {
        target_state: pending.target_state,
        reason_code: reason,
        note: note || null,
      });
      toast.success(`Advanced → ${pending.target_state}`);
      onCompleted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="board-drop-dialog">
        <DialogHeader>
          <DialogTitle>
            Advance case → <span className="uppercase font-mono text-brand">
              {pending.target_state.replace(/_/g, ' ')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {pending.case_number} · {pending.sku_code} @ {pending.location_code} ·
            dropped from <span className="uppercase font-mono">{pending.from_state}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Reason code *</Label>
            <select
              className="w-full h-9 rounded border border-border bg-background px-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="board-drop-reason"
            >
              {(reasonCodes?.[pending.target_state] || ['unspecified']).map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context…" data-testid="board-drop-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="board-drop-submit">
            {busy ? 'Advancing…' : 'Confirm advance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DiscrepancyBoard() {
  const [board, setBoard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [watcherBusy, setWatcherBusy] = useState(false);
  const [dragging, setDragging] = useState(null);      // { case_, from_state }
  const [dragOverCol, setDragOverCol] = useState(null); // target state string
  const [pendingDrop, setPendingDrop] = useState(null); // { case_id, case_number, sku_code, location_code, from_state, target_state }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([getDiscrepancyBoard(50), listCaseTemplates()]);
      setBoard(b.data);
      setTemplates(t.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runWatcher = async () => {
    setWatcherBusy(true);
    try {
      const r = await runDiscrepancySlaWatcher();
      const d = r.data;
      toast.success(`SLA scan: ${d.breaches} breach(es) across ${d.scanned_cases} case(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Watcher failed');
    } finally {
      setWatcherBusy(false);
    }
  };

  // ---- Drag-and-drop handlers -----------------------------------------------
  // Cards call these; columns act as drop targets. All state-machine validation
  // happens locally against `board.allowed_transitions` so an invalid drop
  // never even hits the API.
  const handleCardDragStart = (e, case_) => {
    setDragging({ case_, from_state: case_.state });
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', case_.id);
    } catch (_) { /* jsdom + some browsers throw on setData; safe to ignore */ }
  };
  const handleCardDragEnd = () => {
    setDragging(null);
    setDragOverCol(null);
  };
  const isValidDrop = (targetState) => {
    if (!dragging) return false;
    if (targetState === dragging.from_state) return false;
    const allowed = (board.allowed_transitions || {})[dragging.from_state] || [];
    return allowed.includes(targetState);
  };
  const handleColumnDragOver = (e, targetState) => {
    if (!dragging) return;
    // preventDefault is REQUIRED for onDrop to fire.
    e.preventDefault();
    if (isValidDrop(targetState)) {
      e.dataTransfer.dropEffect = 'move';
      if (dragOverCol !== targetState) setDragOverCol(targetState);
    } else {
      e.dataTransfer.dropEffect = 'none';
      if (dragOverCol !== `invalid:${targetState}`) setDragOverCol(`invalid:${targetState}`);
    }
  };
  const handleColumnDragLeave = (targetState) => {
    if (dragOverCol === targetState || dragOverCol === `invalid:${targetState}`) {
      setDragOverCol(null);
    }
  };
  const handleColumnDrop = (e, targetState) => {
    e.preventDefault();
    const drag = dragging;
    setDragOverCol(null);
    setDragging(null);
    if (!drag) return;
    if (targetState === drag.from_state) return;
    const allowed = (board.allowed_transitions || {})[drag.from_state] || [];
    if (!allowed.includes(targetState)) {
      const allowedTxt = allowed.length
        ? allowed.map((s) => s.replace(/_/g, ' ')).join(' or ')
        : 'nothing';
      toast.error(
        `Cannot drop from ${drag.from_state.replace(/_/g, ' ')} → ${targetState.replace(/_/g, ' ')}. Allowed: ${allowedTxt}.`
      );
      return;
    }
    setPendingDrop({
      case_id: drag.case_.id,
      case_number: drag.case_.case_number,
      sku_code: drag.case_.sku_code,
      location_code: drag.case_.location_code,
      from_state: drag.from_state,
      target_state: targetState,
    });
  };

  if (!board) {
    return (
      <div className="text-sm text-muted-foreground p-4" data-testid="board-loading">
        {loading ? 'Loading Kanban…' : 'Board unavailable'}
      </div>
    );
  }

  const totals = board.totals || {};

  return (
    <div className="space-y-3" data-testid="discrepancy-board">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-xs" data-testid="board-total-open">
          {totals.open || 0} open
        </Badge>
        <Badge className="text-xs bg-red-100 text-red-800 border-red-300" data-testid="board-total-breaches">
          <Warning size={12} className="mr-1" />
          {totals.breaches_open || 0} SLA breach(es)
        </Badge>
        <Badge variant="outline" className="text-xs" data-testid="board-total-closed">
          {totals.closed_14d || 0} closed / 14d
        </Badge>
        <Badge variant="outline" className="text-xs" data-testid="board-total-templates">
          <Lightning size={12} className="mr-1" weight="fill" />
          {templates.length} template(s)
        </Badge>
        <div className="text-[10px] text-muted-foreground hidden md:block" data-testid="board-dnd-hint">
          · Drag a card into another column to advance it
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="board-refresh">
            <ArrowClockwise size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={runWatcher} disabled={watcherBusy} data-testid="board-run-sla">
            <Warning size={14} className="mr-1" /> {watcherBusy ? 'Scanning…' : 'Run SLA scan'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)} data-testid="board-manage-templates">
            <Lightning size={14} className="mr-1" weight="fill" /> Templates
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSlaOpen(true)} data-testid="board-edit-sla">
            <Gear size={14} className="mr-1" /> SLA config
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
        {board.states.map((state) => {
          const cards = board.columns[state] || [];
          const stat = board.stats?.[state] || {};
          const isTerminal = state === 'closed' || state === 'approved';
          const isDropValid = dragOverCol === state;
          const isDropInvalid = dragOverCol === `invalid:${state}`;
          const dropClass = isDropValid
            ? 'ring-2 ring-brand bg-brand/5'
            : isDropInvalid
              ? 'ring-2 ring-red-400 bg-red-50/40'
              : '';
          return (
            <div
              key={state}
              className={`bg-slate-50 rounded-lg p-2 border border-slate-200 min-h-[240px] transition-colors ${dropClass}`}
              data-testid={`board-column-${state}`}
              data-drop-target={dragging ? (isValidDrop(state) ? 'valid' : 'invalid') : 'idle'}
              onDragOver={(e) => handleColumnDragOver(e, state)}
              onDragLeave={() => handleColumnDragLeave(state)}
              onDrop={(e) => handleColumnDrop(e, state)}
            >
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wide">
                    {STATE_LABELS[state] || state}
                  </div>
                  <Badge variant="outline" className="text-[10px]">{stat.count || 0}</Badge>
                </div>
                <div className="text-[10px] text-muted-foreground">{STATE_HINTS[state]}</div>
                {stat.breaches > 0 && !isTerminal && (
                  <div className="text-[10px] text-red-700 mt-0.5 font-medium">
                    {stat.breaches} past SLA
                  </div>
                )}
                {dragging && isValidDrop(state) && (
                  <div className="text-[10px] text-brand font-medium mt-0.5" data-testid={`board-drop-hint-${state}`}>
                    Drop to advance
                  </div>
                )}
                {dragging && isDropInvalid && (
                  <div className="text-[10px] text-red-700 mt-0.5" data-testid={`board-drop-invalid-${state}`}>
                    Cannot skip states
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {cards.length === 0 && (
                  <div className="text-[11px] text-muted-foreground italic p-2 text-center">
                    No cases
                  </div>
                )}
                {cards.map((c) => (
                  <CaseCard
                    key={c.id}
                    case_={c}
                    allowedTransitions={board.allowed_transitions || {}}
                    reasonCodes={board.reason_codes || {}}
                    templates={templates}
                    onTransitioned={load}
                    onDragStart={handleCardDragStart}
                    onDragEnd={handleCardDragEnd}
                    isDragging={dragging?.case_?.id === c.id}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <SlaEditor open={slaOpen} onClose={() => setSlaOpen(false)} onSaved={load} />
      <TemplateManager
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        states={board.states}
        reasonCodes={board.reason_codes}
        onChanged={load}
      />
      <DropTransitionDialog
        pending={pendingDrop}
        reasonCodes={board.reason_codes}
        onClose={() => setPendingDrop(null)}
        onCompleted={load}
      />
    </div>
  );
}
